import type { TRPCClient } from 'src/utils/hook-factories';

/**
 * Args accepted by every `useGet*Count` badge hook (COI-908).
 *
 * `entityIds` is passed by ALL of them, unconditionally — that uniformity is
 * what lets React Query dedupe 19 hooks onto one in-flight request without
 * `httpBatchLink` (pinned to `maxItems: 1` per COI-451). Whether a given badge
 * is actually narrowed by the selection is decided server-side, in the
 * data-layer `navigation-count-repository`; sending it here never widens or
 * narrows a badge on its own.
 *
 * `where` is the GraphQL-branch equivalent and is declared per hook, since only
 * the 8 entity-filtered badges have one.
 */
export type NavigationCountArgs = {
  entityIds?: string[];
};

/**
 * The one procedure every badge hook's tRPC branch points at. Keep this the
 * single call site: two hooks passing different inputs would fork the query key
 * and reintroduce the fan-out this replaced.
 */
export const navigationCountsQueryOptions = (
  trpc: TRPCClient,
  queryArgs: NavigationCountArgs | undefined
) =>
  trpc.frontend.navigation.counts.queryOptions({
    entityIds: queryArgs?.entityIds,
  });

/**
 * Maps one collapsed count onto a GraphQL `*_aggregate` root.
 *
 * A `null` count means that badge's query failed server-side (the data layer
 * catches each count individually so one failure cannot blank the whole nav).
 * Mapping it to a null `aggregate` — rather than `{ count: 0 }` — makes
 * `ConnectedCount` read `undefined`, which `Count` renders as a blank badge
 * instead of a misleading zero.
 */
export const toAggregate = (count: number | null) =>
  count === null ? null : { count };
